import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  useSortBy,
  useFilters,
  useGlobalFilter,
  usePagination,
} from "react-table";
import ColumnFilter from "./ColumnFilter";
import GlobalFilter from "./GlobalFilter";
import { AiOutlineFilePdf } from "react-icons/ai";

function PassportTable({ data, columns, onExportPDF, extraControls }) {
  const [filterData, setFilterData] = useState([]);
  const defaultColumn = useMemo(() => {
    return {
      Filter: ColumnFilter,
    };
  }, []);
  
  // Use data directly passed from parent (already filtered)
  useEffect(() => {
    setFilterData(data || []);
  }, [data]);
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    { columns, data: filterData || [], defaultColumn },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  const handleDetails = (profile) => {};

  useEffect(() => {
    setPageSize(20);
  }, [setPageSize]);

  return (
    <>
      {/* On mobile */}
      {/* <div className='w-full md:hidden flex flex-col justify-center overflow-auto'> 
        {
          data.map(row => {
            return (
               <div className="w-full my-5 p-6 bg-black-gradient border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                  <div className='flex items-center justify-between'>
                    <p className='text-2xl font-bold py-2 '>
                      ID: {row.id}
                    </p>
                    <p className='text-lg py-2 mr-4'>
                      Percentage: {row.termId}
                    </p>
                  </div>

                  <div className='border-r-2 border-gray-300'>
                      <h5 className="mb-2 pt-2   text-lg font-semibold tracking-tight text-gray-300- dark:text-white">Reason for Loan ?</h5>
                      <p className="mb-5 font-normal text-gray-400">{row.loanFor}</p>
                      <p className='text-lg py-2'>
                        Borrowed: {row.amount.toLocaleString('en-MW', { style: 'currency', currency: 'MWK' })}
                      </p>
                      <p className='text-lg py-2 mb-2'>
                        Payback: {row.paybackAmount.toLocaleString('en-MW', { style: 'currency', currency: 'MWK' })}
                      </p>
                  </div>
                  <div 
                    className="inline-flex items-center text-blue-600 hover:underline p-2 cursor-pointer text-lg mb-3"
                    onClick={() => handleDetails(row)}
                  >
                      More Details
                      <svg className='w-5 ml-3' fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z">
                        </path><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"></path>
                        </svg>
                  </div>
                </div>
            )
          })
        }

      </div> */}

      <div className="w-full flex flex-col bg-white shadow-lg rounded-lg p-3">
        {/* Toolbar */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-72">
            <GlobalFilter
              filter={globalFilter}
              setFilter={setGlobalFilter}
              placeholderText="Search Profiles"
              className="w-full pl-10 pr-4 py-2 rounded-full shadow focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700 text-sm border border-gray-300 bg-white"
            />
            <div className="absolute top-1/2 left-3 -translate-y-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="10" cy="10" r="7" />
                <line x1="21" y1="21" x2="15" y2="15" />
              </svg>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {extraControls}
            {onExportPDF && (
              <button
                onClick={() => onExportPDF(filterData)}
                disabled={!filterData || filterData.length === 0}
                className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md shadow font-medium ${
                  !filterData || filterData.length === 0 ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                <AiOutlineFilePdf fontSize={16} /> Export PDF
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto rounded border border-gray-100">
          <table {...getTableProps()} className="w-full min-w-[600px] border-collapse text-sm">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-100 text-gray-600 uppercase text-xs">
                  {headerGroup.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      className="px-3 py-2 border-b border-gray-200 font-semibold text-left whitespace-nowrap"
                    >
                      {col.render("Header")}
                      <span>{col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : ""}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-3 py-3 text-gray-700 text-sm whitespace-nowrap">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-3 text-sm text-gray-600">
          <span>Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong></span>
          <div className="flex gap-1">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">{"<<"}</button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">Previous</button>
            <button onClick={() => nextPage()} disabled={!canNextPage} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">Next</button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-40 text-xs">{">>"}</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default PassportTable;
